import { Bowser } from '@factorin/util';
import React, { useState, useCallback, useMemo } from 'react';

import { UI2Migration } from 'Modules/ui2/components';
import { cn } from 'Modules/ui2/utils';
import { useTranslation } from 'Modules/core/i18n';
import { Portal } from 'Modules/ui';

import { CryptoProImport, NoCertificateComponent } from './CryptoProImport';
import { SuccessfulRegistrationComponent, FailedRegistrationComponent } from './FormComponent';
import { PlatformNotSupportedComponent, BrowserNotSupportedComponent } from './ErrorsAndWarnings';
import { ResendLetter } from './ResendLetter';
import { Lead } from './Lead';
import { Rate } from './Rate';
import { Form } from './Form';
import { SuccessLite } from './SuccessLite';
import { FailureLite } from './FailureLite';
import { FailureINNLite } from './FailureINNLite';
import { supportedBrowsers } from '../../constants';
import styles from './index.module.scss';


const bowser = new Bowser();

const stages = {
    cryptoProImport: CryptoProImport,
    lead: Lead,
    rate: Rate,
    form: Form,
    success: SuccessfulRegistrationComponent,
    failure: FailedRegistrationComponent,
    platformNotSuported: PlatformNotSupportedComponent,
    browserNotSuported: BrowserNotSupportedComponent,
    noCertificate: NoCertificateComponent,
    resendLetter: ResendLetter,
    successLite: SuccessLite,
    failureLite: FailureLite,
    failureINNLite: FailureINNLite,
};

const initCheckStages = stages => {
    if (bowser.tablet)
        return stages.platformNotSuported;

    if (!bowser.satisfies(supportedBrowsers))
        return stages.browserNotSuported;

    return stages.lead;
};

const getProgress = stage => {
    if (stage === stages.lead)
        return '0';
    if (stage === stages.rate)
        return '50%';
    return '100%';
};

/** @type {React.FC<{ landingOwnerUid?: string; className?: string; }>} */
export const FactorinRegistrationForm = ({ landingOwnerUid, className, children }) => {
    const { t } = useTranslation('registrationForm');
    const [{ Component, ...componentProps }, setState] = useState(() => ({
        Component: initCheckStages(stages),
        landingOwnerUid,
    }));
    const setStage = useCallback(
        (stage, props) => setState(state => ({
            ...state,
            Component: stages[stage],
            ...props,
        })),
        [],
    );

    const width = useMemo(() => getProgress(Component), [Component]);

    return (
        <UI2Migration className={cn(styles.form, className)}>
            <Portal>
                <div className={styles.progress} style={{ width }} />
            </Portal>
            {children}
            <Component t={t} {...componentProps} setStage={setStage} />
        </UI2Migration>
    );
};
